import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Form } from "antd";

import { StepTwoFormContext } from "./StepTwoFormContext";
import { WizardDataFormContext } from "../../../../Wizard/contexts/WizardDataForm";

import { ActividadesComercio } from "../../../../../../../../../shared/interfaces";
import { StepTwoCommerceForm } from "../../../../interfaces/steps-forms.interface";

import { LOADING_SAVED_QUOTATIONS_STATES, QuotationInfoContext, QuotationStep } from "../../../../../shared";
import { LOADING_RENEWAL_QUOTATION_STATES } from "../../../../../shared/interfaces/loading-renewal-quotation-states-enum";

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {

  const [stepTwoForm] = Form.useForm<StepTwoCommerceForm>();

    const { 
      savedQuotation, 
      loadingSavedQuotation, 
      currentStep, 
      loadSaveQuotationState, 
      setLoadSaveQuotationState,
      setRenewalQuotationState,
      renewalQuotationState,
      quotationToRenewal 
    } = useContext(QuotationInfoContext);

  const { rubricList, getRubricList } = useContext(WizardDataFormContext);

  const [rubricSelected, setRubricSelected] = useState<ActividadesComercio>();

  const fillStepTwoServices = useCallback(async () => {
    getRubricList();
  }, [getRubricList]);

  const getRubricKey = useCallback(
    (rubric: ActividadesComercio) =>
      `${rubric.capituloTarifa}${rubric.capituloTarifaInciso1}${rubric.capituloTarifaInciso2}`,
    []
  );

  const findRubricFromQuotation = useCallback(
    (quotation: any): ActividadesComercio | undefined => {
      const valorInciso1 = quotation?.capituloDeTarifaInciso1 ?? '--';
      const valorInciso2 = quotation?.capituloDeTarifaInciso2 ?? '--';
      const rubric = `${quotation?.capituloDeTarifa}${valorInciso1}${valorInciso2}`;

      return rubricList?.actividadesComercio.find(
        (item: ActividadesComercio) => getRubricKey(item) === rubric
      );
    },
    [rubricList, getRubricKey]
  );

  const fillStepTwoData = useCallback(async () => {
    const rubricSelected = findRubricFromQuotation(savedQuotation);

    stepTwoForm.setFieldValue(
      'category',
      rubricSelected ? getRubricKey(rubricSelected) : savedQuotation.rubroSeleccionado
    );

    setRubricSelected(rubricSelected);
  }, [stepTwoForm, savedQuotation, findRubricFromQuotation, getRubricKey]);

  const fillStepTwoDataRenewal = useCallback(async () => {
    const rubricSelected = findRubricFromQuotation(quotationToRenewal);

    stepTwoForm.setFieldValue(
      'category',
      rubricSelected ? getRubricKey(rubricSelected) : quotationToRenewal.rubroSeleccionado
    );

    setRubricSelected(rubricSelected);
  }, [stepTwoForm, quotationToRenewal, findRubricFromQuotation, getRubricKey]);

  const restoreRubricFromSavedQuotation = useCallback(() => {
    const quotation = savedQuotation ?? quotationToRenewal;
    if (!quotation || !rubricList?.actividadesComercio?.length) return;

    const foundRubric = findRubricFromQuotation(quotation);

    if (foundRubric) setRubricSelected(foundRubric);
  }, [savedQuotation, quotationToRenewal, rubricList, findRubricFromQuotation]);

  useEffect(() => {
    if (
      (currentStep === QuotationStep.TWO ||
        currentStep === QuotationStep.FOUR) &&
      !rubricSelected &&
      rubricList
    ) {
      restoreRubricFromSavedQuotation();
    }
  }, [currentStep, rubricSelected, rubricList, restoreRubricFromSavedQuotation]);

  useEffect(() => {
    if (currentStep === QuotationStep.TWO && !rubricList && !loadingSavedQuotation) {
      getRubricList();
    }
  }, [currentStep, rubricList, loadingSavedQuotation]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING);
      fillStepTwoServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING &&
      rubricList !== undefined
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED);
    }
  }, [loadSaveQuotationState, rubricList]);
  
  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING);
      fillStepTwoData();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING &&
      rubricSelected) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED);
    }
  }, [loadSaveQuotationState, rubricSelected]);

  /**
   * 
   * Codigo para renovacion de hogar
   * 
   */

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL);
      fillStepTwoServices();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL &&
      rubricList !== undefined
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL);
    }
  }, [renewalQuotationState, rubricList]);
  
  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL);
      fillStepTwoDataRenewal();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL &&
      rubricSelected) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL);
    }
  }, [renewalQuotationState, rubricSelected]);


  const values = useMemo(
    () => ({
      stepTwoForm,
      rubricSelected,
      setRubricSelected
    }),
    [
      stepTwoForm,
      rubricSelected,
      setRubricSelected
    ]
  );

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};
